import { distanceInWordsToNow, parse } from 'date-fns';
import { Formik } from 'formik';
import { capitalize } from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useMemo } from 'react';

import CancelButton from 'components/buttons/Cancel';
import ReportButton from 'components/buttons/Report';
import Card from 'components/Card';
import CardActions from 'components/Card/Actions';
import CardHeader from 'components/Card/Header';
import CardText from 'components/Card/Text';
import Dialog from 'components/Dialog';
import EnhancedTextarea from 'components/EnhancedTextarea';
import LoadBlur from 'components/LoadBlur';
import RowSpacer from 'components/RowSpacer';

import validationSchema from './validationSchema';
import { convertToLocalDate } from '../../utils/helper';

const styles = {
  card: {},
};

const FlagDialog = ({
  flagged,
  flaggedDate,
  flaggedReason,
  onRequestClose,
  onSubmit,
  open = false,
  saving = false,
  type = 'content',
}) => {
  const initialValues = useMemo(
    () => ({
      reason: flaggedReason || '',
    }),
    [flaggedReason],
  );
  const renderForm = useCallback(
    ({
      dirty,
      errors,
      handleBlur,
      handleChange,
      isSubmitting,
      isValid,
      submitForm,
      values,
    }) => {
      const canSave = dirty && isValid && !isSubmitting;
      return (
        <Fragment>
          <CardText noTopPadding>
            <EnhancedTextarea
              autoFocus
              disabled={isSubmitting}
              errorText={errors?.reason}
              name='reason'
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder='Please be as specific and detailed as possible'
              value={values.reason}
            />
          </CardText>
          <CardActions noTopPadding>
            <CancelButton forceLabel secondary onClick={onRequestClose} />
            <RowSpacer />
            <ReportButton
              disabled={!canSave}
              forceLabel
              primary={canSave}
              onClick={submitForm}
            />
          </CardActions>
        </Fragment>
      );
    },
    [onRequestClose],
  );
  return (
    <Dialog onRequestClose={onRequestClose} open={open}>
      <Card style={styles.card}>
        <LoadBlur loading={saving}>
          <CardHeader
            title={
              flagged ? `${capitalize(type)} reported` : `Report this ${type}`
            }
            subheader={
              flaggedDate
                ? `${distanceInWordsToNow(
                    convertToLocalDate(parse(flaggedDate)),
                  )} ago`
                : null
            }
          />
          <CardText noTopPadding>
            {flagged
              ? 'You said:'
              : `Why are you reporting this ${type} to us?`}
          </CardText>
          {flagged ? (
            <Fragment>
              {flaggedReason ? (
                <CardText noTopPadding>
                  <blockquote>{flaggedReason}</blockquote>
                </CardText>
              ) : null}
              <CardActions noTopPadding>
                <RowSpacer />
                <CancelButton
                  forceLabel
                  label={flagged ? 'Close' : 'Cancel'}
                  secondary
                  onClick={onRequestClose}
                />
              </CardActions>
            </Fragment>
          ) : (
            <Formik
              isInitialValid
              initialValues={initialValues}
              onSubmit={onSubmit}
              validateOnBlur={false}
              validationSchema={validationSchema}
            >
              {renderForm}
            </Formik>
          )}
        </LoadBlur>
      </Card>
    </Dialog>
  );
};

FlagDialog.propTypes = {
  flagged: PropTypes.bool,
  flaggedDate: PropTypes.string,
  flaggedReason: PropTypes.string,
  onRequestClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool,
  saving: PropTypes.bool,
  type: PropTypes.string,
};

export default FlagDialog;
